/**
 * Request Card Component
 *
 * Mobile-optimized card display for expenditure requests
 * Shows key info with expandable details
 */

import { renderIcon, type IconName } from '../utils/icons';
import { formatAmount, formatDate } from '../utils/formatters';
import { createInlineApprovalActions } from './inline-approval-actions';
import type { ExpenditureRequest } from './request-list';

export interface RequestCardConfig {
  request: ExpenditureRequest;
  showStatus?: boolean;
  showCreator?: boolean;
  showActions?: boolean;
  isPendingApproval?: boolean;
  enableBulkSelection?: boolean;
  onRequestClick?: (request: ExpenditureRequest) => void;
  onActionComplete?: () => void;
}

interface StatusConfig {
  label: string;
  icon: IconName;
  colorClass: string;
  bgClass: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  DRAFT: {
    label: 'Draft',
    icon: 'edit',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/50',
  },
  PENDING: {
    label: 'Pending',
    icon: 'clock',
    colorClass: 'text-warning-foreground',
    bgClass: 'bg-warning/20',
  },
  APPROVED: {
    label: 'Approved',
    icon: 'checkCircle',
    colorClass: 'text-success-foreground',
    bgClass: 'bg-success/20',
  },
  REJECTED: {
    label: 'Rejected',
    icon: 'xCircle',
    colorClass: 'text-destructive-foreground',
    bgClass: 'bg-destructive/20',
  },
  CANCELLED: {
    label: 'Cancelled',
    icon: 'x',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/50',
  },
};

function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function createRequestCard(config: RequestCardConfig): HTMLElement {
  const {
    request,
    showStatus = true,
    showCreator = false,
    showActions = false,
    isPendingApproval = false,
    enableBulkSelection = false,
    onRequestClick,
    onActionComplete,
  } = config;

  const card = document.createElement('div');
  card.className = 'glass-sm rounded-lg p-4 mb-3 transition-all hover:bg-muted/20';
  card.setAttribute('data-request-id', request.id);

  const statusConfig = (STATUS_CONFIG[request.status] || STATUS_CONFIG.DRAFT)!;

  // Card Header
  const header = document.createElement('div');
  header.className = 'flex items-start gap-3 mb-3';

  // Checkbox (if bulk selection enabled)
  if (enableBulkSelection) {
    const checkboxWrapper = document.createElement('div');
    checkboxWrapper.className = 'pt-0.5';
    checkboxWrapper.innerHTML = `
      <input type="checkbox" class="row-checkbox w-4 h-4 rounded border-input bg-background" data-id="${request.id}" />
    `;
    checkboxWrapper.onclick = (e) => e.stopPropagation();
    header.appendChild(checkboxWrapper);
  }

  // Title and metadata
  const titleSection = document.createElement('div');
  titleSection.className = 'flex-1 min-w-0';

  const titleRow = document.createElement('div');
  titleRow.className = 'flex items-start justify-between gap-2 mb-1';

  const title = document.createElement('h3');
  title.className = 'font-medium text-foreground line-clamp-2 flex-1';
  title.textContent = request.title || 'Untitled';
  titleRow.appendChild(title);

  // Status badge
  if (showStatus) {
    const statusBadge = document.createElement('span');
    statusBadge.className = `inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgClass} ${statusConfig.colorClass}`;
    statusBadge.innerHTML = `
      ${renderIcon(statusConfig.icon, { size: 'xs' }).outerHTML}
      ${statusConfig.label}
    `;
    titleRow.appendChild(statusBadge);
  }

  titleSection.appendChild(titleRow);

  // Metadata row
  const metaRow = document.createElement('div');
  metaRow.className = 'flex items-center gap-3 text-sm text-muted-foreground';

  // Amount
  const amountSpan = document.createElement('span');
  amountSpan.className = 'font-semibold text-foreground';
  amountSpan.textContent = formatAmount(request.amount, request.currency);
  metaRow.appendChild(amountSpan);

  // Date
  const dateSpan = document.createElement('span');
  dateSpan.className = 'flex items-center gap-1';
  dateSpan.innerHTML = `
    ${renderIcon('calendar', { size: 'xs' }).outerHTML}
    ${formatDate(request.created_at)}
  `;
  metaRow.appendChild(dateSpan);

  // Creator (if shown)
  if (showCreator && request.creator_name) {
    const creatorSpan = document.createElement('span');
    creatorSpan.className = 'flex items-center gap-1 truncate';
    creatorSpan.innerHTML = `
      ${renderIcon('user', { size: 'xs' }).outerHTML}
      <span class="truncate">${escapeHtml(request.creator_name)}</span>
    `;
    metaRow.appendChild(creatorSpan);
  }

  titleSection.appendChild(metaRow);

  // Approver role (if applicable)
  if (request.approver_role) {
    const approverNote = document.createElement('div');
    approverNote.className = 'text-xs text-muted-foreground mt-1';
    approverNote.textContent = `Approving as ${request.approver_role}`;
    titleSection.appendChild(approverNote);
  }

  header.appendChild(titleSection);

  card.appendChild(header);

  // Expandable details section
  const detailsSection = document.createElement('div');
  detailsSection.className = 'overflow-hidden transition-all duration-200 max-h-0';
  detailsSection.setAttribute('data-expanded', 'false');

  const detailsContent = document.createElement('div');
  detailsContent.className = 'pt-3 border-t border-border mt-3';

  // Description (if available)
  if (request.description) {
    const description = document.createElement('p');
    description.className = 'text-sm text-muted-foreground mb-3 line-clamp-3';
    description.textContent = request.description;
    detailsContent.appendChild(description);
  }

  // Action buttons (for pending approvals)
  if (isPendingApproval && showActions) {
    const actionsWrapper = document.createElement('div');
    actionsWrapper.className = 'flex gap-2';
    actionsWrapper.onclick = (e) => e.stopPropagation();

    const actionButtons = createInlineApprovalActions({
      request: {
        id: request.id,
        title: request.title,
        amount: request.amount,
        currency: request.currency,
      },
      onActionComplete: onActionComplete || (() => {}),
    });
    actionsWrapper.appendChild(actionButtons);
    detailsContent.appendChild(actionsWrapper);
  }

  // View details button
  const viewButton = document.createElement('button');
  viewButton.className = 'text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1 mt-2';
  viewButton.innerHTML = `
    View Details
    ${renderIcon('chevronRight', { size: 'xs' }).outerHTML}
  `;
  viewButton.onclick = (e) => {
    e.stopPropagation();
    if (onRequestClick) {
      onRequestClick(request);
    } else {
      // Default navigation
      window.history.pushState({}, '', `/requests/${request.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };
  detailsContent.appendChild(viewButton);

  detailsSection.appendChild(detailsContent);
  card.appendChild(detailsSection);

  // Expand/collapse toggle button
  const toggleButton = document.createElement('button');
  toggleButton.className = 'w-full pt-2 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors';
  toggleButton.setAttribute('aria-expanded', 'false');
  toggleButton.setAttribute('aria-label', 'Toggle details');
  toggleButton.innerHTML = renderIcon('chevronDown', { size: 'sm' }).outerHTML;

  toggleButton.onclick = (e) => {
    e.stopPropagation();
    const isExpanded = detailsSection.getAttribute('data-expanded') === 'true';

    if (isExpanded) {
      detailsSection.style.maxHeight = '0';
      detailsSection.setAttribute('data-expanded', 'false');
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.innerHTML = renderIcon('chevronDown', { size: 'sm' }).outerHTML;
    } else {
      detailsSection.style.maxHeight = detailsSection.scrollHeight + 'px';
      detailsSection.setAttribute('data-expanded', 'true');
      toggleButton.setAttribute('aria-expanded', 'true');
      toggleButton.innerHTML = renderIcon('chevronUp', { size: 'sm' }).outerHTML;
    }
  };

  card.appendChild(toggleButton);

  // Card click handler (navigate to details)
  card.onclick = () => {
    if (onRequestClick) {
      onRequestClick(request);
    } else {
      window.history.pushState({}, '', `/requests/${request.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  return card;
}